/**
 * Error Handling Utilities for SchemaTable Component
 * Implements graceful degradation with console warning logging
 */

import { errorHandlerLogger } from './logger'

/**
 * Error types for categorizing different kinds of failures
 */
export const ERROR_TYPES = {
  SCHEMA_VALIDATION: 'schema-validation',
  FORMATTER_EXECUTION: 'formatter-execution', 
  ACTION_HANDLER: 'action-handler',
  DATA_TYPE_MISMATCH: 'data-type-mismatch',
  COMPONENT_RENDER: 'component-render',
  PERFORMANCE: 'performance'
};

/**
 * Error severity levels
 */
export const ERROR_SEVERITY = {
  CRITICAL: 'critical',   // Breaks component functionality
  WARNING: 'warning',     // Degraded functionality but component works
  INFO: 'info'           // Informational only
};

/**
 * Safely execute formatter function with error handling
 * @param {Function} formatter - Formatter function to execute
 * @param {any} value - Value to format
 * @param {Object} row - Complete row data (optional)
 * @param {Object} column - Column configuration (optional)
 * @returns {Object} Result with { success, value, error }
 */
export function safeExecuteFormatter(formatter, value, row = null, column = null) {
  try {
    if (typeof formatter !== 'function') {
      return {
        success: false,
        value: String(value || ''),
        error: 'Formatter is not a function'
      };
    }

    const result = formatter(value, row, column);
    
    return {
      success: true,
      value: result !== null && result !== undefined ? String(result) : String(value || ''),
      error: null
    };
  } catch (error) {
    logError(ERROR_TYPES.FORMATTER_EXECUTION, ERROR_SEVERITY.WARNING, {
      message: 'Formatter function execution failed',
      formatterName: formatter.name || 'anonymous',
      value: value,
      error: error.message,
      column: column && column.name
    });

    // Return fallback value
    return {
      success: false,
      value: String(value || ''), 
      error: error.message
    };
  }
}

/**
 * Safely execute action condition function
 * @param {Function} condition - Condition function to execute
 * @param {Object} row - Row data
 * @param {number} index - Row index
 * @returns {Object} Result with { success, result, error }
 */
export function safeExecuteCondition(condition, row, index) {
  try {
    if (typeof condition !== 'function') {
      return { success: true, result: true, error: null }; // Default to visible
    }

    const result = condition(row, index);
    
    return {
      success: true,
      result: Boolean(result),
      error: null
    };
  } catch (error) {
    logError(ERROR_TYPES.ACTION_HANDLER, ERROR_SEVERITY.WARNING, {
      message: 'Action condition function execution failed',
      conditionName: condition.name || 'anonymous',
      rowIndex: index,
      error: error.message
    });

    // Default to visible on error
    return {
      success: false,
      result: true,
      error: error.message
    };
  }
}

/**
 * Parse data value with optimistic type conversion
 * @param {any} value - Raw value to parse
 * @param {string} expectedType - Expected data type
 * @returns {Object} Result with { success, value, originalValue, warning }
 */
export function parseDataValue(value, expectedType) {
  const originalValue = value;
  
  if (value === null || value === undefined) {
    return {
      success: true,
      value: '',
      originalValue,
      warning: null
    };
  }

  try {
    let parsedValue = value;
    let warning = null;

    switch (expectedType) {
      case 'string':
        parsedValue = String(value);
        if (typeof value !== 'string') {
          warning = `Expected string, got ${typeof value}. Converted to "${parsedValue}"`;
        }
        break;

      case 'number':
        if (typeof value === 'number') {
          parsedValue = value;
        } else if (typeof value === 'string') {
          const numValue = parseFloat(value);
          if (!isNaN(numValue)) {
            parsedValue = numValue;
            warning = `Converted string "${value}" to number ${numValue}`;
          } else {
            parsedValue = 0;
            warning = `Cannot convert "${value}" to number, using 0`;
          }
        } else {
          parsedValue = 0;
          warning = `Expected number, got ${typeof value}. Using 0`;
        }
        break;

      case 'boolean':
        if (typeof value === 'boolean') {
          parsedValue = value;
        } else if (typeof value === 'string') {
          const lowerValue = value.toLowerCase();
          if (['true', '1', 'yes', 'on'].includes(lowerValue)) {
            parsedValue = true;
            warning = `Converted string "${value}" to boolean true`;
          } else if (['false', '0', 'no', 'off'].includes(lowerValue)) {
            parsedValue = false;
            warning = `Converted string "${value}" to boolean false`;
          } else {
            parsedValue = Boolean(value);
            warning = `Converted "${value}" to boolean ${parsedValue}`;
          }
        } else {
          parsedValue = Boolean(value);
          warning = `Expected boolean, got ${typeof value}. Converted to ${parsedValue}`;
        }
        break;

      case 'date':
        if (value instanceof Date) {
          parsedValue = value;
        } else if (typeof value === 'string' || typeof value === 'number') {
          const dateValue = new Date(value);
          if (!isNaN(dateValue.getTime())) {
            parsedValue = dateValue;
            if (typeof value !== 'string') {
              warning = `Converted ${typeof value} to date`;
            }
          } else {
            parsedValue = new Date();
            warning = `Cannot parse "${value}" as date, using current date`;
          }
        } else {
          parsedValue = new Date();
          warning = `Expected date, got ${typeof value}. Using current date`;
        }
        break;

      case 'currency':
        // Same as number but with additional parsing for currency strings
        if (typeof value === 'number') {
          parsedValue = value;
        } else if (typeof value === 'string') {
          // Remove currency symbols and parse
          const cleanValue = value.replace(/[£$€,\s]/g, '');
          const numValue = parseFloat(cleanValue);
          if (!isNaN(numValue)) {
            parsedValue = numValue;
            if (cleanValue !== value) {
              warning = `Parsed currency string "${value}" to number ${numValue}`;
            }
          } else {
            parsedValue = 0;
            warning = `Cannot parse currency "${value}", using 0`;
          }
        } else {
          parsedValue = 0;
          warning = `Expected currency (number), got ${typeof value}. Using 0`;
        }
        break;

      default:
        parsedValue = String(value);
        warning = `Unknown data type "${expectedType}", converting to string`;
    }

    if (warning) {
      logError(ERROR_TYPES.DATA_TYPE_MISMATCH, ERROR_SEVERITY.WARNING, {
        message: 'Data type mismatch with optimistic parsing',
        expectedType,
        actualType: typeof originalValue,
        originalValue,
        parsedValue,
        warning
      });
    }

    return {
      success: true,
      value: parsedValue,
      originalValue,
      warning
    };
  } catch (error) {
    logError(ERROR_TYPES.DATA_TYPE_MISMATCH, ERROR_SEVERITY.WARNING, {
      message: 'Data parsing failed',
      expectedType,
      value: originalValue,
      error: error.message
    });

    return {
      success: false,
      value: String(originalValue || ''),
      originalValue,
      warning: `Failed to parse value: ${error.message}`
    };
  }
}

/**
 * Create error state configuration for component rendering
 * @param {string} message - User-friendly error message
 * @param {Object} details - Additional error details
 * @returns {Object} Error state configuration
 */
export function createErrorState(message, details = {}) {
  return {
    isError: true,
    message: message || 'An error occurred',
    details: details,
    timestamp: new Date().toISOString(),
    showRetry: details.retryable || false
  };
}

/**
 * Log error with structured format for developer debugging
 * @param {string} type - Error type from ERROR_TYPES
 * @param {string} severity - Error severity from ERROR_SEVERITY  
 * @param {Object} details - Error details and context
 */
export function logError(type, severity, details) {
  const logData = {
    type,
    severity,
    timestamp: new Date().toISOString(),
    component: 'SchemaTable',
    ...details
  };

  const prefix = getLogPrefix(severity);
  const message = `${prefix} [${type}] ${details.message || 'Unknown error'}`;

  switch (severity) {
    case ERROR_SEVERITY.CRITICAL:
      errorHandlerLogger.error(message, logData);
      break;
    case ERROR_SEVERITY.WARNING:
      errorHandlerLogger.warn(message, logData);
      break;
    case ERROR_SEVERITY.INFO:
    default:
      errorHandlerLogger.info(message, logData);
      break;
  }
}

/**
 * Get log prefix emoji for severity level
 * @param {string} severity - Error severity
 * @returns {string} Emoji prefix
 */
function getLogPrefix(severity) {
  switch (severity) {
    case ERROR_SEVERITY.CRITICAL:
      return '🚨';
    case ERROR_SEVERITY.WARNING:
      return '⚠️';
    case ERROR_SEVERITY.INFO:
    default:
      return 'ℹ️';
  }
}

/**
 * Performance monitoring helper
 * @param {string} operation - Operation name
 * @param {Function} fn - Function to execute
 * @param {number} warningThreshold - Time threshold in ms for warning
 * @returns {any} Function result
 */
export function monitorPerformance(operation, fn, warningThreshold = 50) {
  const startTime = performance.now();
  
  try {
    const result = fn();
    const duration = performance.now() - startTime;
    
    if (duration > warningThreshold) {
      logError(ERROR_TYPES.PERFORMANCE, ERROR_SEVERITY.WARNING, {
        message: `Performance warning: ${operation} took longer than expected`,
        operation,
        duration: Math.round(duration),
        threshold: warningThreshold
      });
    }
    
    return result;
  } catch (error) {
    const duration = performance.now() - startTime;
    
    logError(ERROR_TYPES.COMPONENT_RENDER, ERROR_SEVERITY.CRITICAL, {
      message: `Operation failed: ${operation}`,
      operation,
      duration: Math.round(duration),
      error: error.message
    });
    
    throw error;
  }
}

/**
 * Debounce function for performance optimization
 * @param {Function} func - Function to debounce
 * @param {number} wait - Wait time in milliseconds
 * @returns {Function} Debounced function
 */
export function debounce(func, wait) {
  let timeout;
  
  return function executedFunction(...args) {
    const later = () => {
      clearTimeout(timeout);
      func.apply(this, args);
    };
    
    clearTimeout(timeout);
    timeout = setTimeout(later, wait);
  };
}

/**
 * Create retry mechanism with exponential backoff
 * @param {Function} fn - Function to retry
 * @param {number} maxRetries - Maximum number of retries
 * @param {number} initialDelay - Initial delay in ms
 * @returns {Promise} Promise that resolves with function result or rejects after max retries
 */
export async function retryWithBackoff(fn, maxRetries = 3, initialDelay = 100) {
  let lastError;
  
  for (let attempt = 0; attempt <= maxRetries; attempt++) {
    try {
      return await fn();
    } catch (error) {
      lastError = error;
      
      if (attempt === maxRetries) {
        logError(ERROR_TYPES.COMPONENT_RENDER, ERROR_SEVERITY.CRITICAL, {
          message: 'All retry attempts failed',
          attempts: attempt + 1,
          error: error.message
        });
        break;
      }
      
      const delay = initialDelay * Math.pow(2, attempt);
      await new Promise(resolve => setTimeout(resolve, delay));
    }
  }
  
  throw lastError;
}

/**
 * Handle formatter function execution errors with graceful degradation
 * @param {string} message - Error message
 * @param {string} context - Context where error occurred
 * @param {any} value - Original value that failed formatting
 * @param {Error} [error] - Original error object
 */
export function handleFormatterError(message, context = 'formatter', value = null, error = null) {
  const errorInfo = {
    context,
    message,
    originalValue: value,
    valueType: typeof value,
    timestamp: new Date().toISOString()
  }

  if (error) {
    errorInfo.stack = error.stack
    errorInfo.errorName = error.name
  }

  logError(ERROR_TYPES.FORMATTER_EXECUTION, ERROR_SEVERITY.WARNING, {
    message: 'Formatter execution error',
    ...errorInfo
  })
  
  // Optional: Emit to parent component for error tracking
  if (typeof window !== 'undefined' && window.__HONEYCOMB_ERROR_TRACKING__) {
    window.__HONEYCOMB_ERROR_TRACKING__.push({
      type: 'formatter_error',
      ...errorInfo
    })
  }
}

/**
 * Simple warning logger for data type parsing
 * @param {string} message - Warning message to log
 */
export function logWarning(message) {
  errorHandlerLogger.warn(`${message}`)
  
  logError(ERROR_TYPES.DATA_TYPE_MISMATCH, ERROR_SEVERITY.WARNING, {
    message: message,
    timestamp: new Date().toISOString()
  })
}